<template>
	<div>
		<div class="row mb-2">
			<div class="col-12">
				<h1>{{ $t('batch-download') }}</h1>
			</div>
		</div>

		<template v-if="job">
			<div class="row" v-if="job.status === PENDING || job.status === PREPARING">
				<div class="col-12">
					<div class="alert alert-info mt-3">{{ $t('preparing') }}</div>
					<b-progress
						:value="job.completed_count || 0"
						:max="Math.max(job.total_count || 0, 1)"
						variant="primary"
						show-progress
						animated
						class="mb-3"
					></b-progress>
					<p v-if="job.total_count">{{ job.completed_count || 0 }} / {{ job.total_count }}</p>
				</div>
			</div>
			<div class="row" v-else-if="job.status === READY">
				<div class="col-12">
					<div class="alert alert-success mt-3">{{ $t('ready') }}</div>
					<button type="button" class="btn btn-primary mr-2" @click="downloadFile">
						<b-icon icon="download"></b-icon> {{ $t('download') }}
					</button>
				</div>
			</div>
			<div class="row" v-else-if="job.status === FAILED">
				<div class="col-12">
					<div class="alert alert-danger mt-3">{{ $t('something-went-wrong') }}</div>
				</div>
			</div>
			<div class="row mt-3">
				<div class="col-12">
					<button type="button" class="btn btn-secondary" @click="backToList">
						{{ $t('back') }}
					</button>
				</div>
			</div>
		</template>

		<template v-else-if="showPicker">
			<div class="row" v-if="!loadingStudents">
				<div class="col-12">
					<label>{{ $t('select-students') }}</label>
					<EventStudentEditor v-model="selectedStudents" :options="students" :showStudentIds="true" />
				</div>
			</div>
			<div class="row" v-else>
				<div class="col-12">
					<div class="skeleton midnight h32 mb-2"></div>
					<div class="skeleton midnight h32 mb-2"></div>
				</div>
			</div>
			<div class="row mt-3">
				<div class="col-12">
					<button type="button" class="btn btn-secondary mr-2" @click="showPicker = false">
						{{ $t('back') }}
					</button>
					<button type="button" class="btn btn-primary" :disabled="!selectedStudents.length" @click="confirmDownload">
						{{ $t('confirm') }}
					</button>
				</div>
			</div>
		</template>

		<template v-else>
			<div class="row mb-3">
				<div class="col-12">
					<button type="button" class="btn btn-primary" @click="startNew">
						{{ $t('new') }}
					</button>
				</div>
			</div>
			<div class="row" v-if="loadingJobs">
				<div class="col-12">
					<div class="skeleton midnight h32 mb-2"></div>
				</div>
			</div>
			<div class="row" v-else-if="!jobs.length">
				<div class="col-12">
					{{ $t('no-results') }}
				</div>
			</div>
			<div class="row" v-else>
				<div class="col-12">
					<table class="table">
						<thead>
							<tr>
								<th>{{ $t('created') }}</th>
								<th>{{ $t('status') }}</th>
								<th></th>
							</tr>
						</thead>
						<tbody>
							<tr v-for="pastJob in jobs" :key="pastJob.id">
								<td>{{ pastJob.created }}</td>
								<td>
									{{ statusLabel(pastJob.status) }}
									<b-progress
										v-if="pastJob.status === PENDING || pastJob.status === PREPARING"
										:value="pastJob.completed_count || 0"
										:max="Math.max(pastJob.total_count || 0, 1)"
										variant="primary"
										show-progress
										animated
										style="min-width: 8rem"
									></b-progress>
								</td>
								<td>
									<button
										type="button"
										class="btn btn-sm btn-primary"
										v-if="pastJob.status === READY"
										@click="downloadJob(pastJob)"
									>
										<b-icon icon="download"></b-icon> {{ $t('download') }}
									</button>
									<button
										type="button"
										class="btn btn-sm btn-secondary"
										v-else-if="pastJob.status === PENDING || pastJob.status === PREPARING"
										@click="viewJob(pastJob)"
									>
										{{ $t('view') }}
									</button>
								</td>
							</tr>
						</tbody>
					</table>
				</div>
			</div>
		</template>
	</div>
</template>

<script>
import EventStudentEditor from "src/components/Events/EventStudentEditor.vue";

const PENDING = 2;
const PREPARING = 3;
const READY = 1;
const FAILED = 4;

export default {
	components: {
		EventStudentEditor
	},
	data() {
		return {
			PENDING,
			PREPARING,
			READY,
			FAILED,
			showPicker: false,
			loadingStudents: false,
			studentsLoaded: false,
			students: [],
			selectedStudents: [],
			job: null,
			jobs: [],
			loadingJobs: false
		};
	},
	created() {
		if (!this.$UserSession.hasPriv.call(this, 'TIMELINE_BATCH_DOWNLOAD')) {
			this.$router.replace('/timeline/search');
			return;
		}
		this.syncWithRoute();
	},
	watch: {
		'$route.params.jobid'() {
			this.syncWithRoute();
		}
	},
	methods: {
		syncWithRoute() {
			const jobid = this.$route.params.jobid;
			if (jobid) {
				this.showPicker = false;
				this.job = { id: jobid, status: PENDING };
				this.pollStatus();
			} else {
				this.job = null;
				this.listJobs();
			}
		},
		statusLabel(status) {
			if (status === READY) return this.$t('ready');
			if (status === FAILED) return this.$t('failed');
			return this.$t('preparing');
		},
		listJobs() {
			this.loadingJobs = true;
			this.$cfetch(`${this.$settings.APIBaseURL}/timeline/batchdownload/list`, {
				method: 'POST',
				data: {}
			}).then(res => {
				this.loadingJobs = false;
				if (this.$route.params.jobid) return;
				if (res.status !== 'SUCCESS') return;
				this.jobs = res.results;
				const stillRunning = this.jobs.some(pastJob => pastJob.status === PENDING || pastJob.status === PREPARING);
				if (stillRunning) {
					window.setTimeout(() => {
						if (!this.$route.params.jobid) this.listJobs();
					}, 3000);
				}
			}).catch(err => {
				this.loadingJobs = false;
				this.$log.error.call(this, err);
			});
		},
		startNew() {
			this.showPicker = true;
			if (!this.studentsLoaded) {
				this.getStudents();
			}
		},
		getStudents() {
			this.loadingStudents = true;
			this.$users.getStudents.call(this).then(users => {
				this.students = users.map(user => {
					return {
						id: user.id,
						displayname: user.displayName,
						student_id: user.studentid,
						programme_id: user.programme,
						firm_id: user.firm,
						year: user.year,
						group_id: user.group
					};
				});
				this.studentsLoaded = true;
				this.loadingStudents = false;
			});
		},
		confirmDownload() {
			if (!this.selectedStudents.length) return;
			this.$cfetch(`${this.$settings.APIBaseURL}/timeline/batchdownload/start`, {
				method: 'POST',
				data: {
					user_ids: this.selectedStudents.map(student => student.id).join(',')
				}
			}).then(res => {
				if (res.status !== 'SUCCESS') {
					this.$log.error.call(this, res);
					return;
				}
				this.$router.push(`/timeline/batchdownload/${res.result.id}`);
			}).catch(err => {
				this.$log.error.call(this, err);
			});
		},
		pollStatus() {
			this.$cfetch(`${this.$settings.APIBaseURL}/timeline/batchdownload/status`, {
				method: 'POST',
				data: {
					id: this.job.id
				}
			}).then(res => {
				if (res.status !== 'SUCCESS') {
					this.job = { ...this.job, status: FAILED };
					return;
				}
				this.job = res.result;
				if (this.job.status === PENDING || this.job.status === PREPARING) {
					window.setTimeout(() => this.pollStatus(), 3000);
				}
			}).catch(err => {
				this.$log.error.call(this, err);
				this.job = { ...this.job, status: FAILED };
			});
		},
		downloadFile() {
			this.downloadJob(this.job);
		},
		downloadJob(pastJob) {
			const body = {
				username: this.$UserSession.getValue("username"),
				authToken: this.$UserSession.getValue("authToken"),
				id: pastJob.id
			};
			const url = `${this.$settings.APIBaseURL}/timeline/batchdownload/file?` + new URLSearchParams(body).toString();
			window.open(url);
		},
		viewJob(pastJob) {
			this.$router.push(`/timeline/batchdownload/${pastJob.id}`);
		},
		backToList() {
			this.$router.push('/timeline/batchdownload');
		}
	}
};
</script>
